import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  File,
  Upload,
  Search,
  FileText,
  Grid3x3,
  List,
  Home,
  ChevronRight,
  Activity,
  Loader2,
  Folder,
  Trash2Icon,
  Globe,
  Table2,
  FolderPlus,
  Pencil,
  FolderInput,
  Cloud,
  Plug,
} from "lucide-react";
import {
  BsFilePdfFill,
  BsFileEarmarkWordFill,
  BsFileEarmarkSpreadsheetFill,
  BsFileEarmarkTextFill,
  BsImage,
} from "react-icons/bs";

import documentStoresService, {
  DocumentStoreTypeEnums,
} from "../../services/document-stores.service";
import uploadedFilesService, {
  UploadedFile,
} from "../../services/uploaded-files.service";
import UploadModal from "../../components/documents/UploadModal";
import PreviewModal from "../../components/documents/PreviewModal";
import FileLogsModal from "../../components/documents/FileLogsModal";
import Tooltip from "../../widgets/ToolTip";
import Can from "../../components/auth/Can";
import Loader from "../../widgets/Loader";
import { toast } from "react-hot-toast";
import DeleteConfirmModal from "../../components/documents/DeleteConfirmModal";
import FolderFormModal, {
  FolderFormMode,
} from "../../components/documents/FolderFormModal";
import MoveFolderModal, {
  MoveFolderTreeNode,
} from "../../components/documents/MoveFolderModal";
import foldersService, {
  Folder as FolderRecord,
  FolderSource,
} from "../../services/folders.service";

interface FileItem {
  id: string;
  name: string;
  type: "file" | "folder";
  size: number;
  modified: string;
  extension?: string;
  thumbnail?: string;
  fileData?: UploadedFile;
  children?: FileItem[];
  // Folder-only fields. folderId is set ONLY for real Folder records
  // (unified branch). Legacy virtual folders leave it undefined and are
  // not addressable for rename/move/delete.
  folderId?: string;
  parentFolderId?: string | null;
  source?: FolderSource;
  // Counts surfaced by /folder/utils/list for folder rows. Files leave
  // these undefined.
  subfolderCount?: number;
  fileCount?: number;
}

interface BreadcrumbItem {
  name: string;
  path: FileItem[];
}

const FileManagerUI = () => {
  const { documentStoreId } = useParams<{ documentStoreId: string }>();
  const navigate = useNavigate();
  const [folderName, setFolderName] = useState<string>("Loading...");
  const [files, setFiles] = useState<FileItem[]>([]);
  const [moveTree, setMoveTree] = useState<MoveFolderTreeNode[]>([]);
  const [moveTreeLoading, setMoveTreeLoading] = useState(false);
  // Store-mode is detected once at mount based on whether the file list
  // response contains legacy "virtual" folder nodes (type:"folder" with no
  // _id). Tree-mode stores keep their full converted hierarchy in
  // legacyTree and navigate in-memory; unified stores hit the API per click.
  const [storeMode, setStoreMode] = useState<"loading" | "unified" | "tree">(
    "loading",
  );
  const [legacyTree, setLegacyTree] = useState<FileItem[]>([]);
  const [currentFolderPath, setCurrentFolderPath] = useState<FileItem[]>([]); // Current navigation path
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]); // Breadcrumb trail
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [, setShowLogsModal] = useState(false);
  const [selectedFileForLogs, setSelectedFileForLogs] =
    useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [documentStoreType, setDocumentStoreType] = useState<string>(
    DocumentStoreTypeEnums.LOCAL,
  );
  const pollingIntervalRef = useRef<any | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileItem | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  // Folder operation state
  const [folderFormOpen, setFolderFormOpen] = useState(false);
  const [folderFormMode, setFolderFormMode] =
    useState<FolderFormMode>("create");
  const [folderFormTarget, setFolderFormTarget] = useState<FileItem | null>(
    null,
  );
  const [folderFormLoading, setFolderFormLoading] = useState(false);

  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveModalTarget, setMoveModalTarget] = useState<FileItem | null>(null);
  const [moveModalLoading, setMoveModalLoading] = useState(false);
  const [moveModalError, setMoveModalError] = useState<string | null>(null);

  const [showFolderDeleteModal, setShowFolderDeleteModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<FileItem | null>(null);
  const [isDeletingFolder, setIsDeletingFolder] = useState(false);

  const currentFolderId: string | null =
    currentFolderPath.length > 0
      ? (currentFolderPath[currentFolderPath.length - 1].folderId ?? null)
      : null;

  // True when the user is currently inside a folder that has no real
  // Folder record (legacy virtual folder), or anywhere in a tree-mode
  // store below root. Uploads need a real folder_id to land in the right
  // place, so we disable them in this state.
  const isInVirtualSubtree =
    currentFolderPath.length > 0 && currentFolderPath.some((p) => !p.folderId);
  // parentModified is applied ONLY when `item` is a folder — folders have
  // no `updatedAt` of their own in the legacy tree response so we inherit
  // a timestamp from the caller. Files always use their own `item.updatedAt`
  // and ignore this parameter.
  const convertToFileItem = (item: any, parentModified?: string): FileItem => {
    if (item.type === "folder") {
      const hasRealId = typeof item._id === "string" && item._id.length > 0;
      const children: FileItem[] =
        item.children?.map((child: any) =>
          convertToFileItem(child, parentModified),
        ) || [];
      // Virtual folders have no backend-supplied counts; derive from
      // children so the cards still surface "N folders · M files".
      const derivedSubfolderCount = hasRealId
        ? undefined
        : children.filter((c) => c.type === "folder").length;
      const derivedFileCount = hasRealId
        ? undefined
        : children.filter((c) => c.type === "file").length;
      return {
        // Real folders use _id; legacy virtual folders fall back to name.
        id: hasRealId ? item._id : item.name,
        name: item.name,
        type: "folder",
        size: 0,
        modified: parentModified || new Date().toLocaleDateString(),
        folderId: hasRealId ? item._id : undefined,
        parentFolderId: hasRealId ? (item.parent_folder_id ?? null) : undefined,
        source: hasRealId ? (item.source as FolderSource) : undefined,
        subfolderCount: derivedSubfolderCount,
        fileCount: derivedFileCount,
        children,
      };
    } else {
      const fileExtension = item.name.split(".").pop()?.toLowerCase() || "";

      // Ensure URL has protocol
      let fileUrl = item.url;
      if (
        fileUrl &&
        !fileUrl.startsWith("http://") &&
        !fileUrl.startsWith("https://")
      ) {
        fileUrl = `https://${fileUrl}`;
      }
      return {
        id: item._id,
        name: item.name,
        type: "file",
        size: item.file_size,
        modified: new Date(item.updatedAt).toLocaleDateString(),
        extension: fileExtension,
        thumbnail: fileUrl, // Keeping thumbnail data, but not used for display
        fileData: { ...item, url: fileUrl },
      };
    }
  };

  // Navigate to a folder. Behaviour depends on store mode:
  // - tree mode (legacy cloud import) → walk in-memory `children`, no API.
  // - unified mode (real Folder record) → fetch the folder via API.
  const navigateToFolder = (folder: FileItem) => {
    // Tree mode: every folder is virtual or treated as such. Walk children.
    if (storeMode === "tree") {
      if (!folder.children) return;
      const newPath = [...currentFolderPath, folder];
      setCurrentFolderPath(newPath);
      setBreadcrumbs([
        { name: folderName, path: [] },
        ...newPath.map((item, index) => ({
          name: item.name,
          path: newPath.slice(0, index + 1),
        })),
      ]);
      setFiles(folder.children);
      if (visibleFilesNeedPolling(folder.children)) startStatusPolling();
      return;
    }

    // Unified mode: only real folders are addressable.
    if (!folder.folderId) return;
    const newPath = [...currentFolderPath, folder];
    setCurrentFolderPath(newPath);
    setBreadcrumbs([
      { name: folderName, path: [] },
      ...newPath.map((item, index) => ({
        name: item.name,
        path: newPath.slice(0, index + 1),
      })),
    ]);
    fetchFolderContents(folder.folderId);
  };

  // Navigate using breadcrumb. Same branch as navigateToFolder.
  const navigateToBreadcrumb = (breadcrumb: BreadcrumbItem) => {
    setCurrentFolderPath(breadcrumb.path);

    const breadcrumbIndex = breadcrumbs.findIndex(
      (b) => b.name === breadcrumb.name,
    );
    setBreadcrumbs(breadcrumbs.slice(0, breadcrumbIndex + 1));

    if (storeMode === "tree") {
      // Walk legacyTree along the path; show the children of the target.
      if (breadcrumb.path.length === 0) {
        setFiles(legacyTree);
        if (visibleFilesNeedPolling(legacyTree)) startStatusPolling();
        return;
      }
      const target = breadcrumb.path[breadcrumb.path.length - 1];
      const children = target.children || [];
      setFiles(children);
      if (visibleFilesNeedPolling(children)) startStatusPolling();
      return;
    }

    const targetFolderId =
      breadcrumb.path.length === 0
        ? null
        : (breadcrumb.path[breadcrumb.path.length - 1].folderId ?? null);
    fetchFolderContents(targetFolderId);
  };

  const getFileCategory = (file: FileItem) => {
    const ext = file.extension?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "pdf";
      case "csv":
        return "csv";
      case "doc":
      case "docx":
        return "word";
      case "png":
      case "jpg":
      case "jpeg":
        return "image";
      case "txt": // Added explicit txt category
        return "txt";
      default:
        return "other";
    }
  };

  const categories = [
    {
      id: "all",
      label: "All Files",
      icon: File,
      count: files.length,
      color: "bg-gray-100 text-gray-700 border-gray-200",
    },
    {
      id: "pdf",
      label: "PDF",
      icon: BsFilePdfFill, // Changed to specific icon
      count: files.filter((f) => getFileCategory(f) === "pdf").length,
      color: "bg-red-100 text-red-700 border-red-200",
    },
    {
      id: "csv",
      label: "CSV",
      icon: BsFileEarmarkSpreadsheetFill, // Changed to specific icon
      count: files.filter((f) => getFileCategory(f) === "csv").length,
      color: "bg-green-100 text-green-700 border-green-200",
    },
    {
      id: "word",
      label: "Word",
      icon: BsFileEarmarkWordFill, // Changed to specific icon
      count: files.filter((f) => getFileCategory(f) === "word").length,
      color: "bg-blue-100 text-blue-700 border-blue-200",
    },
    {
      id: "image", // Changed ID to "image"
      label: "Images", // Changed label to be more general
      icon: BsImage,
      count: files.filter((f) => getFileCategory(f) === "image").length,
      color: "bg-purple-100 text-purple-700 border-purple-200", // New color for images
    },
    {
      id: "txt", // New category for text files
      label: "Text Files",
      icon: BsFileEarmarkTextFill,
      count: files.filter((f) => getFileCategory(f) === "txt").length,
      color: "bg-gray-100 text-gray-700 border-gray-200",
    },
    {
      id: "other", // Catch-all for other types
      label: "Other Files",
      icon: File,
      count: files.filter((f) => getFileCategory(f) === "other").length,
      color: "bg-gray-100 text-gray-700 border-gray-200",
    },
  ];

  const getFileIcon = (file: FileItem) => {
    if (file.type === "folder") {
      return Folder;
    }
    return FileText;
  };

  const getColoredFileIcon = (file: FileItem) => {
    // Handle folders
    if (file.type === "folder") {
      return {
        Icon: Folder,
        colorClass: "text-yellow-500",
      };
    }

    const ext = file.extension?.toLowerCase();
    let Icon: any; // Initialize without a default
    let colorClass = "text-gray-600"; // Default color for generic files

    switch (ext) {
      case "pdf":
        Icon = BsFilePdfFill;
        colorClass = "text-red-600";
        break;
      case "csv":
        Icon = BsFileEarmarkSpreadsheetFill;
        colorClass = "text-green-600";
        break;
      case "doc":
      case "docx":
        Icon = BsFileEarmarkWordFill;
        colorClass = "text-blue-600";
        break;
      case "png":
      case "jpg":
      case "jpeg":
        Icon = BsImage; // Use BsImage for image files
        colorClass = "text-purple-600"; // A distinct color for images
        break;
      case "txt": // Explicitly handle .txt files
        Icon = BsFileEarmarkTextFill;
        colorClass = "text-gray-600";
        break;
      default:
        Icon = FileText; // Use Lucide's FileText as a generic fallback for other unhandled files
        colorClass = "text-gray-400"; // A slightly lighter gray for truly generic/unknown files
    }

    return {
      Icon,
      colorClass,
    };
  };

  // Human-readable count summary for a folder card — uses the counts
  // surfaced by /folder/utils/list. Falls back to "—" when the backend
  // didn't include them (e.g. legacy responses).
  const formatFolderCounts = (folder: FileItem): string => {
    const sub = folder.subfolderCount;
    const fil = folder.fileCount;
    if (sub === undefined && fil === undefined) return "—";
    const parts: string[] = [];
    if (sub && sub > 0) parts.push(`${sub} folder${sub === 1 ? "" : "s"}`);
    if (fil && fil > 0) parts.push(`${fil} file${fil === 1 ? "" : "s"}`);
    if (parts.length === 0) return "Empty";
    return parts.join(" · ");
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get status badge color and text
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "DONE":
        return { color: "bg-green-100 text-green-700", text: "Done" };
      case "FAILED":
        return { color: "bg-red-100 text-red-700", text: "Failed" };
      case "IN_PROGRESS":
      case "PROCESSING":
        return { color: "bg-blue-100 text-blue-700", text: "Processing" };
      case "PENDING":
        return { color: "bg-yellow-100 text-yellow-700", text: "Pending" };
      default:
        return {
          color: "bg-gray-100 text-gray-700",
          text: status || "Unknown",
        };
    }
  };

  // Check if polling is needed for status updates
  const startStatusPolling = () => {
    // Clear any existing polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Set up new polling interval
    const pollStatus = async () => {
      if (!documentStoreId) return;

      // Use setFiles callback to get the latest files state
      setFiles((currentFiles) => {
        // Get files that are not DONE or FAILED
        const processingFiles = currentFiles.filter(
          (file) =>
            file.fileData?.overall_processed_status &&
            file.fileData.overall_processed_status !== "DONE" &&
            file.fileData.overall_processed_status !== "FAILED",
        );

        // If no files are processing, stop polling
        if (processingFiles.length === 0) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return currentFiles;
        }

        // Only call API with processing file IDs
        const fileIds = processingFiles.map((f) => f.id);

        // Make the API call
        uploadedFilesService
          .getStatusByIds({
            file_ids: fileIds,
            document_store_id: documentStoreId,
          })
          .then((response) => {
            // Check if response has valid data (data is an array directly)
            if (response?.data && Array.isArray(response.data)) {
              // Update file statuses in state
              setFiles((prevFiles) => {
                const updatedFiles = prevFiles.map((file) => {
                  const statusUpdate = response.data.find(
                    (f) => f._id === file.id,
                  );
                  if (statusUpdate && file.fileData) {
                    return {
                      ...file,
                      fileData: {
                        ...file.fileData,
                        overall_processed_status:
                          statusUpdate.overall_processed_status,
                        visual_processing_status:
                          statusUpdate.visual_processing_status,
                        rag_processing_status:
                          statusUpdate.rag_processing_status,
                      },
                    };
                  }
                  return file;
                });

                // Check if all files are now DONE or FAILED
                const stillProcessing = updatedFiles.some(
                  (file) =>
                    file.fileData?.overall_processed_status &&
                    file.fileData.overall_processed_status !== "DONE" &&
                    file.fileData.overall_processed_status !== "FAILED",
                );

                // If no files are processing anymore, stop polling
                if (!stillProcessing && pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                }

                return updatedFiles;
              });
            }
          })
          .catch((error) => {
            console.error("Error polling file statuses:", error);
          });

        return currentFiles;
      });
    };

    // Start polling interval (every 3 seconds)
    pollingIntervalRef.current = setInterval(pollStatus, 3000);
  };

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleFileSelect = (fileId: string, event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      target.tagName === "INPUT" ||
      target.closest('input[type="checkbox"]')
    ) {
      setSelectedFiles((prev) =>
        prev.includes(fileId)
          ? prev.filter((id) => id !== fileId)
          : [...prev, fileId],
      );
    } else {
      const file = files.find((f) => f.id === fileId);
      if (file) {
        if (file.type === "folder") {
          // Navigate into folder
          navigateToFolder(file);
        } else {
          // Preview file
          setPreviewFile(file);
        }
      }
    }
  };

  const handleUpload = async (
    uploadedFiles: FileList,
    accuracy: string,
    processingType: string,
  ) => {
    if (!documentStoreId) return;

    setIsUploading(true);
    // Backend returns invalid_folder_id / folder_document_store_mismatch
    // when the target folder was deleted or moved between when the upload
    // modal was opened and submitted. We dedupe the toast and bounce to
    // root once after Promise.all completes.
    let folderMissing = false;

    try {
      const uploadPromises = Array.from(uploadedFiles).map(async (file) => {
        try {
          await documentStoresService.uploadFile(
            documentStoreId,
            file,
            accuracy,
            documentStoreType,
            processingType,
            currentFolderId,
          );
          toast.success(`${file.name} uploaded successfully!`);
        } catch (error: any) {
          const code = extractErrorCode(error);
          if (
            code === "invalid_folder_id" ||
            code === "folder_document_store_mismatch"
          ) {
            folderMissing = true;
            // Suppress per-file toast; we'll show one consolidated message.
          } else {
            console.error(`Error uploading ${file.name}:`, error);
            toast.error(`Failed to upload ${file.name}`);
          }
          throw error;
        }
      });

      // Collect outcomes without short-circuiting on the first rejection so
      // the folderMissing flag captures every failed call.
      await Promise.allSettled(uploadPromises);

      setShowUploadModal(false);

      if (folderMissing) {
        toast.error("Folder no longer exists, please refresh");
        // Re-detect store mode and bounce to root — the stale folder id
        // would otherwise keep producing the same 400 on the next action.
        setCurrentFolderPath([]);
        setBreadcrumbs([{ name: folderName, path: [] }]);
        setIsLoadingFiles(true);
        try {
          const { mode, rootItems, tree } = await loadStoreRoot();
          setStoreMode(mode);
          setLegacyTree(mode === "tree" ? tree : []);
          setFiles(rootItems);
          if (visibleFilesNeedPolling(rootItems)) startStatusPolling();
        } finally {
          setIsLoadingFiles(false);
        }
      } else {
        await fetchUploadedFiles();
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // True when any visible file has a non-terminal processing status.
  const visibleFilesNeedPolling = (items: FileItem[]): boolean =>
    items.some(
      (item) =>
        item.fileData?.overall_processed_status &&
        item.fileData.overall_processed_status !== "DONE" &&
        item.fileData.overall_processed_status !== "FAILED",
    );

  // Recursively check a list response for any virtual folder
  // (type:"folder" with no _id). Anywhere in the tree.
  const hasVirtualFolder = (items: any[]): boolean => {
    for (const item of items ?? []) {
      if (item?.type === "folder") {
        if (!item._id) return true;
        if (Array.isArray(item.children) && hasVirtualFolder(item.children)) {
          return true;
        }
      }
    }
    return false;
  };

  // Load the store root and detect its mode (unified vs tree). Used at
  // mount and after any mutation that could change the mode (e.g. delete).
  // In tree mode the full converted tree is cached as legacyTree so
  // subsequent navigation can walk it without further API calls.
  const loadStoreRoot = async (): Promise<{
    mode: "unified" | "tree";
    rootItems: FileItem[];
    tree: FileItem[];
  }> => {
    if (!documentStoreId) {
      return { mode: "unified", rootItems: [], tree: [] };
    }

    // Step 1: fetch the tree response. This alone tells us the store mode
    // (presence of any virtual folder ⇒ tree mode).
    const fileResponse = await uploadedFilesService.getUploadedFiles({
      document_store_id: documentStoreId,
    });
    const rawFiles = fileResponse.data?.uploadedFilesList ?? [];
    const lastModified = fileResponse.data?.last_modified;
    const isLegacy = hasVirtualFolder(rawFiles);

    if (isLegacy) {
      // Tree mode: legacyTree is built from this response; the folder
      // endpoint isn't needed (and would have been discarded).
      const tree: FileItem[] = rawFiles.map((item: any) =>
        convertToFileItem(item, lastModified),
      );
      return { mode: "tree", rootItems: tree, tree };
    }

    // Step 2: unified mode only — fetch real folders (with subfolder/file
    // counts) from the folder endpoint.
    const folderChildren = await foldersService
      .listChildren({
        document_store_id: documentStoreId,
        parent_folder_id: null,
      })
      .catch((err) => {
        console.error("Error listing folders:", err);
        return [] as FolderRecord[];
      });

    const folderItems: FileItem[] = folderChildren.map((f) => ({
      id: f._id,
      folderId: f._id,
      parentFolderId: f.parent_folder_id ?? null,
      source: f.source,
      name: f.name,
      type: "folder" as const,
      size: 0,
      modified: new Date(
        f.updatedAt || f.createdAt || Date.now(),
      ).toLocaleDateString(),
      subfolderCount: f.subfolder_count,
      fileCount: f.file_count,
    }));
    const rootFileItems: FileItem[] = rawFiles
      .filter((item: any) => item.type !== "folder")
      .map((item: any) => convertToFileItem(item, lastModified));
    return {
      mode: "unified",
      rootItems: [...folderItems, ...rootFileItems],
      tree: [],
    };
  };

  // Walk the legacyTree along a path of FileItems, returning the
  // refreshed path (or whatever survived). Used to restore the user's
  // current location after a tree refresh.
  const refreshLegacyPath = (
    tree: FileItem[],
    path: FileItem[],
  ): FileItem[] => {
    const refreshed: FileItem[] = [];
    let cursor = tree;
    for (const step of path) {
      const match = cursor.find((n) => n.id === step.id);
      if (!match) break;
      refreshed.push(match);
      cursor = match.children || [];
    }
    return refreshed;
  };

  // Fetch the direct contents (subfolders + files) of a specific folder,
  // or the store root when folderId is null. Runs the folder-list and
  // file-list endpoints in parallel.
  const fetchFolderContents = async (folderId: string | null) => {
    if (!documentStoreId) return;

    try {
      setIsLoadingFiles(true);

      const [folderChildren, fileResponse] = await Promise.all([
        foldersService
          .listChildren({
            document_store_id: documentStoreId,
            parent_folder_id: folderId,
          })
          .catch((err) => {
            console.error("Error listing folders:", err);
            return [] as FolderRecord[];
          }),
        uploadedFilesService.getUploadedFiles({
          document_store_id: documentStoreId,
          folder_id: folderId,
        }),
      ]);

      const folderItems: FileItem[] = folderChildren.map((f) => ({
        id: f._id,
        folderId: f._id,
        parentFolderId: f.parent_folder_id ?? null,
        source: f.source,
        name: f.name,
        type: "folder",
        size: 0,
        modified: new Date(
          f.updatedAt || f.createdAt || Date.now(),
        ).toLocaleDateString(),
        subfolderCount: f.subfolder_count,
        fileCount: f.file_count,
      }));

      // The list endpoint returns either a tree (when folder_id is omitted)
      // or a flat array (when folder_id is supplied). With folder_id we get
      // a flat list of files; without it we get the store-root tree which
      // for a unified-folder store will only contain root-level files since
      // folders come from the folder endpoint.
      const rawFiles = fileResponse.data?.uploadedFilesList ?? [];
      const fileItems: FileItem[] = rawFiles
        // Defensive: at root with no folder_id the legacy tree branch may
        // include folder nodes too. We drop those — folders already came
        // from the folder endpoint.
        .filter((item: any) => item.type !== "folder")
        .map((item: any) =>
          convertToFileItem(item, fileResponse.data?.last_modified),
        );

      const combined = [...folderItems, ...fileItems];
      setFiles(combined);

      if (visibleFilesNeedPolling(combined)) {
        startStatusPolling();
      }
    } catch (error) {
      console.error("Error fetching folder contents:", error);
      toast.error("Failed to load files");
    } finally {
      setIsLoadingFiles(false);
    }
  };

  // No-arg shim: refresh the currently-open folder. Branches on store mode.
  const fetchUploadedFiles = async () => {
    if (!documentStoreId) return;
    if (storeMode === "tree") {
      // Re-fetch the tree and restore the user's path against it.
      setIsLoadingFiles(true);
      try {
        const { mode, rootItems, tree } = await loadStoreRoot();
        setStoreMode(mode);
        setLegacyTree(mode === "tree" ? tree : []);

        if (mode === "tree") {
          const refreshed = refreshLegacyPath(tree, currentFolderPath);
          if (refreshed.length === currentFolderPath.length) {
            const target = refreshed[refreshed.length - 1];
            const children = target?.children || [];
            setCurrentFolderPath(refreshed);
            setBreadcrumbs([
              { name: folderName, path: [] },
              ...refreshed.map((item, index) => ({
                name: item.name,
                path: refreshed.slice(0, index + 1),
              })),
            ]);
            setFiles(children);
            if (visibleFilesNeedPolling(children)) startStatusPolling();
          } else {
            // Current path no longer resolves; bounce to root.
            setCurrentFolderPath([]);
            setBreadcrumbs([{ name: folderName, path: [] }]);
            setFiles(rootItems);
            if (visibleFilesNeedPolling(rootItems)) startStatusPolling();
          }
        } else {
          // Store mutated into unified shape (rare but possible) — show root.
          setCurrentFolderPath([]);
          setBreadcrumbs([{ name: folderName, path: [] }]);
          setFiles(rootItems);
          if (visibleFilesNeedPolling(rootItems)) startStatusPolling();
        }
      } catch (err) {
        console.error("Error refreshing legacy tree:", err);
        toast.error("Failed to refresh");
      } finally {
        setIsLoadingFiles(false);
      }
      return;
    }
    await fetchFolderContents(currentFolderId);
  };

  const handleViewLogs = async (file: FileItem) => {
    if (file.fileData && documentStoreId) {
      setSelectedFileForLogs(file.fileData);
      setShowLogsModal(true);

      try {
        // Send the file's folder_id so the response is a flat list of just
        // that folder's contents. Omitting folder_id returns a nested tree
        // and the .find() below would miss any file nested below the root.
        const response = await uploadedFilesService.getUploadedFiles({
          document_store_id: documentStoreId,
          folder_id: file.fileData.folder_id ?? null,
        });

        const uploadedFiles = response.data.uploadedFilesList;

        const updatedFile = uploadedFiles.find((f) => f._id === file.id);

        if (updatedFile) {
          setSelectedFileForLogs(updatedFile);

          setFiles((prevFiles) =>
            prevFiles.map((f) => {
              if (f.id === file.id) {
                return {
                  ...f,
                  name: updatedFile.name,
                  size: updatedFile.file_size,
                  modified: new Date(
                    updatedFile.updatedAt,
                  ).toLocaleDateString(),
                  fileData: updatedFile,
                };
              }
              return f;
            }),
          );
        }
      } catch (error) {
        console.error("Error fetching latest file data:", error);
      }
    }
  };

  const handleCloseLogsModal = () => {
    setShowLogsModal(false);
    setSelectedFileForLogs(null);
  };

  useEffect(() => {
    const fetchFolderDetails = async () => {
      if (documentStoreId) {
        try {
          const documentStore =
            await documentStoresService.getDocumentStoreById(documentStoreId);
          setFolderName(documentStore.folder_name);
          setDocumentStoreType(
            documentStore.document_store_type || DocumentStoreTypeEnums.LOCAL,
          );
          setBreadcrumbs([{ name: documentStore.folder_name, path: [] }]);
        } catch (error) {
          console.error("Error fetching folder details:", error);
          setFolderName("Unknown Folder");
          setBreadcrumbs([{ name: "Unknown Folder", path: [] }]); // Handle error case
        }
      }
    };

    fetchFolderDetails();
  }, [documentStoreId]);

  useEffect(() => {
    if (!documentStoreId) return;
    // Reset navigation state and store mode when the store changes,
    // then load root and detect mode.
    setCurrentFolderPath([]);
    setStoreMode("loading");
    setLegacyTree([]);
    setIsLoadingFiles(true);
    loadStoreRoot()
      .then(({ mode, rootItems, tree }) => {
        setStoreMode(mode);
        setLegacyTree(mode === "tree" ? tree : []);
        setFiles(rootItems);
        const needsPolling = visibleFilesNeedPolling(rootItems);
        if (needsPolling) startStatusPolling();
      })
      .catch((err) => {
        console.error("Error loading store root:", err);
        toast.error("Failed to load files");
      })
      .finally(() => setIsLoadingFiles(false));
  }, [documentStoreId]);

  const allFilteredItems = files.filter((item) => {
    const matchesCategory =
      selectedCategory === "all" || getFileCategory(item) === selectedCategory;
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const displayFolders = allFilteredItems.filter(
    (item) => item.type === "folder",
  );
  const displayFiles = allFilteredItems.filter((item) => item.type === "file");

  const handleDeleteFileClick = (file: FileItem) => {
    setFileToDelete(file);
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete) return;
    setIsDeletingFile(true);
    try {
      await uploadedFilesService.deleteFile(fileToDelete.id);
      toast.success(`File "${fileToDelete.name}" deleted successfully`);
      setShowDeleteModal(false);
      setFileToDelete(null);
      fetchUploadedFiles(); // Refresh list
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Failed to delete file");
    } finally {
      setIsDeletingFile(false);
    }
  };

  // ---- Folder operations ----

  // The backend wraps errors as { success:false, code, error: { error_code,
  // error_description } } but some legacy paths flatten the fields onto
  // response.data directly. Read both shapes.
  const extractErrorCode = (error: any): string | undefined => {
    const data = error?.response?.data;
    return data?.error?.error_code || data?.error_code;
  };
  const extractErrorMessage = (error: any): string | undefined => {
    const data = error?.response?.data;
    return (
      data?.error?.error_description ||
      data?.error_description ||
      data?.message ||
      error?.message
    );
  };

  const openCreateFolderModal = () => {
    setFolderFormMode("create");
    setFolderFormTarget(null);
    setFolderFormOpen(true);
  };

  const openRenameFolderModal = (folder: FileItem) => {
    if (!folder.folderId) return;
    setFolderFormMode("rename");
    setFolderFormTarget(folder);
    setFolderFormOpen(true);
  };

  const handleFolderFormSubmit = async (name: string) => {
    if (!documentStoreId) return;
    // Defensive: the modal disables submit when trimmed name is empty,
    // but guard here too so a programmatic trigger can't reach the BE.
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Folder name is required");
      return;
    }
    setFolderFormLoading(true);
    try {
      if (folderFormMode === "create") {
        await foldersService.createFolder({
          document_store_id: documentStoreId,
          parent_folder_id: currentFolderId,
          name: trimmed,
          source: "MANUAL",
        });
        toast.success(`Folder "${trimmed}" created`);
      } else if (folderFormTarget?.folderId) {
        await foldersService.renameFolder(folderFormTarget.folderId, {
          name: trimmed,
        });
        toast.success(`Folder renamed to "${trimmed}"`);
      }
      setFolderFormOpen(false);
      setFolderFormTarget(null);
      await fetchUploadedFiles();
    } catch (error: any) {
      console.error("Folder form error:", error);
      // Mirror the Documents.tsx pattern — surface all errors as toasts so
      // the user gets feedback consistent with the rest of the app, and
      // leave the modal open so they can edit the name and retry.
      const msg =
        extractErrorMessage(error) ||
        (folderFormMode === "create"
          ? "Failed to create folder"
          : "Failed to rename folder");
      toast.error(msg);
    } finally {
      setFolderFormLoading(false);
    }
  };

  const openMoveFolderModal = async (folder: FileItem) => {
    if (!folder.folderId || !documentStoreId) return;
    setMoveModalTarget(folder);
    setMoveModalError(null);
    setMoveModalOpen(true);
    setMoveTreeLoading(true);
    try {
      const all = await foldersService.listAll(documentStoreId);
      setMoveTree(buildTreeFromFlat(all));
    } catch (err) {
      console.error("Failed to load folder tree:", err);
      setMoveTree([]);
      setMoveModalError("Failed to load folder list");
    } finally {
      setMoveTreeLoading(false);
    }
  };

  const handleMoveFolderSubmit = async (newParentFolderId: string | null) => {
    if (!moveModalTarget?.folderId) return;
    setMoveModalLoading(true);
    setMoveModalError(null);
    try {
      await foldersService.moveFolder(moveModalTarget.folderId, {
        new_parent_folder_id: newParentFolderId,
      });
      toast.success(`Moved "${moveModalTarget.name}"`);
      setMoveModalOpen(false);
      setMoveModalTarget(null);
      await fetchUploadedFiles();
    } catch (error: any) {
      console.error("Move folder error:", error);
      const code = extractErrorCode(error);
      const msg = extractErrorMessage(error);
      if (code === "duplicate_folder_name") {
        setMoveModalError("Destination already has a folder with this name");
      } else if (code === "cyclic_move" || code === "invalid_move") {
        setMoveModalError(
          "Cannot move a folder into itself or its descendants",
        );
      } else if (code === "cross_store_move_not_allowed") {
        setMoveModalError("Cannot move across document stores");
      } else {
        setMoveModalError(msg || "Failed to move folder");
      }
    } finally {
      setMoveModalLoading(false);
    }
  };

  const handleDeleteFolderClick = (folder: FileItem) => {
    if (!folder.folderId) return;
    setFolderToDelete(folder);
    setShowFolderDeleteModal(true);
  };

  const handleConfirmDeleteFolder = async () => {
    if (!folderToDelete?.folderId) return;
    setIsDeletingFolder(true);
    try {
      const result = await foldersService.deleteFolder(folderToDelete.folderId);
      const folderCount = result?.deleted_folder_ids?.length ?? 1;
      const fileCount = result?.deleted_file_ids?.length ?? 0;
      toast.success(
        `Deleted ${folderCount} folder${folderCount === 1 ? "" : "s"}` +
          (fileCount > 0
            ? ` and ${fileCount} file${fileCount === 1 ? "" : "s"}`
            : ""),
      );
      setShowFolderDeleteModal(false);

      // If the currently-open path was deleted, bounce up to the nearest
      // ancestor that survives (or to root).
      const deletedIds = new Set(result?.deleted_folder_ids ?? []);
      const survivingPath = currentFolderPath.filter(
        (p) => !p.folderId || !deletedIds.has(p.folderId),
      );
      if (survivingPath.length !== currentFolderPath.length) {
        setCurrentFolderPath(survivingPath);
        setBreadcrumbs([
          { name: folderName, path: [] },
          ...survivingPath.map((item, index) => ({
            name: item.name,
            path: survivingPath.slice(0, index + 1),
          })),
        ]);
      }

      setFolderToDelete(null);
      // Refresh against the (possibly new) folder explicitly. currentFolderId
      // is captured from the render closure and won't reflect setCurrentFolderPath
      // until the next render.
      const refreshTargetId =
        survivingPath.length > 0
          ? (survivingPath[survivingPath.length - 1].folderId ?? null)
          : null;
      await fetchFolderContents(refreshTargetId);
    } catch (error: any) {
      console.error("Delete folder error:", error);
      toast.error(extractErrorMessage(error) || "Failed to delete folder");
    } finally {
      setIsDeletingFolder(false);
    }
  };

  // Build a tree of all folders inside the current document store from
  // the flat Folder[] returned by /folder/utils/list-all. Used as the
  // destination picker for Move. Loaded on demand when the modal opens.
  const buildTreeFromFlat = (folders: FolderRecord[]): MoveFolderTreeNode[] => {
    const byParent = new Map<string | null, FolderRecord[]>();
    for (const f of folders) {
      const key = (f.parent_folder_id ?? null) as string | null;
      const bucket = byParent.get(key) ?? [];
      bucket.push(f);
      byParent.set(key, bucket);
    }
    const build = (parentId: string | null): MoveFolderTreeNode[] =>
      (byParent.get(parentId) ?? []).map((f) => ({
        folderId: f._id,
        name: f.name,
        children: build(f._id),
      }));
    return build(null);
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <div className="bg-white border-b border-gray-200 p-4 pb-3">
        {" "}
        <div className="flex items-center justify-between">
          <div>
            <nav className="flex items-center space-x-2 text-sm mb-2 overflow-x-auto">
              {" "}
              {/* Reduced mb */}
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
              >
                <Home className="h-4 w-4" />
              </button>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <button
                onClick={() => navigate("/documents")}
                className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
              >
                Documents
              </button>
              <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
              {/* Folder Breadcrumbs */}
              {breadcrumbs.map((breadcrumb, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-2 flex-shrink-0"
                >
                  <button
                    onClick={() => navigateToBreadcrumb(breadcrumb)}
                    className={`transition-colors ${
                      index === breadcrumbs.length - 1
                        ? "text-gray-900 font-medium"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {breadcrumb.name}
                  </button>
                  {index < breadcrumbs.length - 1 && (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {storeMode !== "tree" && (
              <Can permission="documents:upload">
                <button
                  onClick={openCreateFolderModal}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                  type="button"
                >
                  <FolderPlus className="h-4 w-4" />
                  New Folder
                </button>
              </Can>
            )}
            <button
              onClick={() => navigate(`/files/${documentStoreId}/web-scraper`)}
              className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 transition-colors"
              type="button"
            >
              <Globe className="h-4 w-4" />
              Web Scraper
            </button>
            <button
              onClick={() =>
                navigate(`/files/${documentStoreId}/spreadsheet-connector`)
              }
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
              type="button"
            >
              <Table2 className="h-4 w-4" />
              Spreadsheet Connector
            </button>
            {documentStoreId && !isInVirtualSubtree && (
              <Tooltip
                content={
                  currentFolderId
                    ? "Connect external APIs / databases to this folder"
                    : "Connect external APIs / databases to this store"
                }
              >
                <button
                  onClick={() => {
                    const folderName =
                      currentFolderPath.length > 0
                        ? currentFolderPath[currentFolderPath.length - 1].name
                        : "";
                    const params = new URLSearchParams();
                    if (currentFolderId)
                      params.set("folderId", currentFolderId);
                    if (folderName) params.set("folderName", folderName);
                    const qs = params.toString();
                    navigate(
                      `/files/${documentStoreId}/connections${qs ? `?${qs}` : ""}`,
                    );
                  }}
                  className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
                  type="button"
                >
                  <Plug className="h-4 w-4" />
                  Connections
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        {/* Folder Name, Search, Upload, and View Mode - Aligned */}
        <div className="flex items-center justify-between mt-4 mb-2">
          {" "}
          <div className="flex items-center gap-1 mt-2 overflow-x-auto pb-1">
            {" "}
            {/* Added mt, reduced gap, pb */}
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full whitespace-nowrap transition-all border text-sm ${
                    // Reduced px, py, gap, added text-sm
                    isActive
                      ? category.color + " shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <IconComponent className="w-3 h-3" /> {/* Reduced w, h */}
                  <span className="font-medium">{category.label}</span>
                  <span
                    className={`text-xs px-1 py-0.5 rounded-full ${
                      // Reduced px, py
                      isActive ? "bg-white bg-opacity-70" : "bg-gray-100"
                    }`}
                  >
                    {category.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            {" "}
            {/* Reduced gap */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64 text-sm" // Reduced py, width, added text-sm
              />
            </div>
            <Can permission="documents:upload">
              <Tooltip
                content={
                  isInVirtualSubtree
                    ? "Uploads are only available at the store root for imported folders. Re-import to enable uploads inside folders."
                    : "Upload files"
                }
              >
                <button
                  onClick={() => setShowUploadModal(true)}
                  disabled={isInVirtualSubtree}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors text-sm"
                >
                  <Upload className="w-4 h-4" />
                  Upload Files
                </button>
              </Tooltip>
            </Can>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded ${
                  viewMode === "list" ? "bg-white shadow-sm" : ""
                }`}
              >
                <List className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded ${
                  viewMode === "grid" ? "bg-white shadow-sm" : ""
                }`}
              >
                <Grid3x3 className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        {isLoadingFiles ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader />
          </div>
        ) : allFilteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <File className="w-12 h-12 mb-4" />
            <p>No files found</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 text-sm font-medium text-gray-500">
              <div className="col-span-4">Name</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Size</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Modified</div>
              <div className="col-span-1">Actions</div>
            </div>

            {/* Render Folders in List View */}
            {displayFolders.map((file) => {
              const FileIcon = getFileIcon(file);
              const { colorClass } = getColoredFileIcon(file);

              return (
                <div
                  key={file.id}
                  className={`grid grid-cols-12 gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                    selectedFiles.includes(file.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={(e) => handleFileSelect(file.id, e)}
                >
                  <div className="col-span-4 flex items-center gap-3">
                    {/* <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() =>
                        setSelectedFiles((prev) =>
                          prev.includes(file.id)
                            ? prev.filter((id) => id !== file.id)
                            : [...prev, file.id]
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    /> */}
                    <FileIcon className={`w-5 h-5 ${colorClass}`} />
                    <span className="font-medium text-gray-900 truncate hover:text-blue-600">
                      {file.name}
                    </span>
                  </div>

                  <div className="col-span-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600 capitalize">
                      {file.type === "folder"
                        ? "Folder"
                        : file.extension
                          ? file.extension.toUpperCase()
                          : "File"}
                    </span>
                  </div>

                  <div className="col-span-2 text-sm text-gray-600">
                    {file.type === "file"
                      ? formatFileSize(file.size)
                      : formatFolderCounts(file)}
                  </div>

                  <div className="col-span-2 flex items-center gap-2">
                    {file.fileData?.overall_processed_status ? (
                      <>
                        {file.fileData.overall_processed_status !== "DONE" &&
                        file.fileData.overall_processed_status !== "FAILED" ? (
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        ) : null}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            getStatusBadge(
                              file.fileData.overall_processed_status,
                            ).color
                          }`}
                        >
                          {
                            getStatusBadge(
                              file.fileData.overall_processed_status,
                            ).text
                          }
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </div>

                  <div className="col-span-1 text-sm text-gray-600">
                    {new Date(file.modified).toLocaleDateString()}
                  </div>

                  <div className="col-span-1 flex items-center gap-2">
                    {storeMode !== "tree" && file.folderId && (
                      <>
                        <Can permission="documents:upload">
                          <Tooltip content="Rename folder">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openRenameFolderModal(file);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-900 rounded"
                              type="button"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </Can>
                        <Can permission="documents:upload">
                          <Tooltip content="Move folder">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openMoveFolderModal(file);
                              }}
                              className="p-1 text-emerald-600 hover:text-emerald-900 rounded"
                              type="button"
                            >
                              <FolderInput className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </Can>
                        <Can permission="documents:delete">
                          <Tooltip content="Delete folder">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFolderClick(file);
                              }}
                              className="p-1 text-red-600 hover:text-red-900 rounded"
                              type="button"
                            >
                              <Trash2Icon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </Can>
                      </>
                    )}
                    {file.fileData && (
                      <Tooltip content="View logs">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLogs(file);
                          }}
                          className="p-1 text-purple-600 hover:text-purple-900 rounded"
                          type="button"
                        >
                          <Activity className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Render Files in List View */}
            {displayFiles.map((file) => {
              const { Icon: FileDisplayIcon, colorClass } =
                getColoredFileIcon(file);

              return (
                <div
                  key={file.id}
                  className={`grid grid-cols-12 gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
                    selectedFiles.includes(file.id) ? "bg-blue-50" : ""
                  }`}
                  onClick={(e) => handleFileSelect(file.id, e)}
                >
                  <div className="col-span-4 flex items-center gap-3">
                    {/* <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() =>
                        setSelectedFiles((prev) =>
                          prev.includes(file.id)
                            ? prev.filter((id) => id !== file.id)
                            : [...prev, file.id]
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    /> */}
                    <FileDisplayIcon className={`w-5 h-5 ${colorClass}`} />
                    <span className="font-medium text-gray-900 truncate hover:text-blue-600">
                      {file.name}
                    </span>
                  </div>

                  <div className="col-span-2 flex items-center gap-2">
                    <span className="text-sm text-gray-600 capitalize">
                      {file.type === "folder"
                        ? "Folder"
                        : file.extension
                          ? file.extension.toUpperCase()
                          : "File"}
                    </span>
                  </div>

                  <div className="col-span-2 text-sm text-gray-600">
                    {file.type === "file"
                      ? formatFileSize(file.size)
                      : formatFolderCounts(file)}
                  </div>

                  <div className="col-span-2 flex items-center gap-2">
                    {file.fileData?.overall_processed_status ? (
                      <>
                        {file.fileData.overall_processed_status !== "DONE" &&
                        file.fileData.overall_processed_status !== "FAILED" ? (
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        ) : null}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            getStatusBadge(
                              file.fileData.overall_processed_status,
                            ).color
                          }`}
                        >
                          {
                            getStatusBadge(
                              file.fileData.overall_processed_status,
                            ).text
                          }
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </div>

                  <div className="col-span-1 text-sm text-gray-600">
                    {file.modified}
                  </div>

                  <div className="col-span-1 flex items-center gap-2">
                    {file.fileData && (
                      <Tooltip content="View logs">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLogs(file);
                          }}
                          className="p-1 text-purple-600 hover:text-purple-900 rounded"
                          type="button"
                        >
                          <Activity className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-2">
            {/* Folders Section */}
            {displayFolders.length > 0 && (
              <>
                <h2 className="text-sm font-medium text-gray-700 mb-3">
                  Folders
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-4 mb-4">
                  {displayFolders.map((file) => {
                    return (
                      <div
                        key={file.id}
                        className={`bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer overflow-hidden group relative ${
                          selectedFiles.includes(file.id)
                            ? "ring-2 ring-blue-500 shadow-lg"
                            : ""
                        }`}
                        onClick={(e) => handleFileSelect(file.id, e)}
                      >
                        <div className="aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
                          {/* Always render folder icon for folders */}
                          <div className="absolute inset-0 flex items-center justify-center p-2">
                            <Folder className="w-14 h-14 text-yellow-500" />
                          </div>

                          <div className="absolute top-2 left-2 flex items-center gap-1">
                            <div
                              className={`px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 shadow-sm`}
                            >
                              Folder
                            </div>
                            {file.source && file.source !== "MANUAL" && (
                              <Tooltip
                                content={`Imported from ${file.source.replace(
                                  "_",
                                  " ",
                                )}`}
                              >
                                <div className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 shadow-sm flex items-center gap-1 text-xs font-medium">
                                  <Cloud className="w-3 h-3" />
                                </div>
                              </Tooltip>
                            )}
                          </div>

                          {selectedFiles.includes(file.id) && (
                            <div className="absolute top-2 right-2">
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}

                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200"></div>
                        </div>

                        <div className="p-2">
                          <h3
                            className="text-sm font-medium text-gray-900 truncate mb-1"
                            title={file.name}
                          >
                            {file.name}
                          </h3>

                          <div className="flex items-center justify-between gap-2 mt-1">
                            <div className="text-xs text-gray-400">
                              {formatFolderCounts(file)}
                            </div>

                            {/* Status not typically shown for folders, but keeping logic if needed */}
                            {file.fileData?.overall_processed_status && (
                              <div className="flex items-center gap-1">
                                {file.fileData.overall_processed_status !==
                                  "DONE" &&
                                file.fileData.overall_processed_status !==
                                  "FAILED" ? (
                                  <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
                                ) : null}
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    getStatusBadge(
                                      file.fileData.overall_processed_status,
                                    ).color
                                  }`}
                                >
                                  {
                                    getStatusBadge(
                                      file.fileData.overall_processed_status,
                                    ).text
                                  }
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* More Options - Show on Hover */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col gap-2">
                          {storeMode !== "tree" && file.folderId && (
                            <>
                              <Can permission="documents:upload">
                                <Tooltip content="Rename folder">
                                  <button
                                    className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-50 border border-blue-200 transition-all hover:scale-110"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openRenameFolderModal(file);
                                    }}
                                    type="button"
                                  >
                                    <Pencil className="w-3 h-3 text-blue-600" />
                                  </button>
                                </Tooltip>
                              </Can>
                              <Can permission="documents:upload">
                                <Tooltip content="Move folder">
                                  <button
                                    className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-50 border border-emerald-200 transition-all hover:scale-110"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openMoveFolderModal(file);
                                    }}
                                    type="button"
                                  >
                                    <FolderInput className="w-3 h-3 text-emerald-600" />
                                  </button>
                                </Tooltip>
                              </Can>
                              <Can permission="documents:delete">
                                <Tooltip content="Delete folder">
                                  <button
                                    className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 border border-red-200 transition-all hover:scale-110"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFolderClick(file);
                                    }}
                                    type="button"
                                  >
                                    <Trash2Icon className="w-3 h-3 text-red-600" />
                                  </button>
                                </Tooltip>
                              </Can>
                            </>
                          )}
                          {file.fileData && (
                            <Tooltip content="View logs">
                              <button
                                className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-purple-50 border border-purple-200 transition-all hover:scale-110"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewLogs(file);
                                }}
                                type="button"
                              >
                                <Activity className="w-4 h-4 text-purple-600" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Files Section */}
            {displayFiles.length > 0 && (
              <>
                <h2 className="text-sm font-medium text-gray-700 mb-3">
                  Files
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-4">
                  {displayFiles.map((file) => {
                    const category = categories.find(
                      (c) => c.id === getFileCategory(file),
                    );
                    const { Icon: FileDisplayIcon, colorClass } =
                      getColoredFileIcon(file);

                    return (
                      <div
                        key={file.id}
                        className={`bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all cursor-pointer overflow-hidden group relative ${
                          selectedFiles.includes(file.id)
                            ? "ring-2 ring-blue-500 shadow-lg"
                            : ""
                        }`}
                        onClick={(e) => handleFileSelect(file.id, e)}
                      >
                        <div className="aspect-[4/3] bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
                          {/* Always render icon for files */}
                          <div className="absolute inset-0 flex items-center justify-center p-2">
                            <FileDisplayIcon
                              className={`w-14 h-14 ${colorClass}`}
                            />
                          </div>

                          <div className="absolute top-2 left-2">
                            <div
                              className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                category?.color || "bg-gray-100 text-gray-700"
                              } shadow-sm`}
                            >
                              {file.extension?.toUpperCase() || "File"}
                            </div>
                          </div>

                          {selectedFiles.includes(file.id) && (
                            <div className="absolute top-2 right-2">
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}

                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200"></div>
                        </div>

                        <div className="p-2">
                          <h3
                            className="text-sm font-medium text-gray-900 truncate mb-1"
                            title={file.name}
                          >
                            {file.name}
                          </h3>

                          <div className="flex items-center justify-between gap-2 mt-1">
                            <div className="text-xs text-gray-400">
                              {formatFileSize(file.size)}
                            </div>

                            {file.fileData?.overall_processed_status && (
                              <div className="flex items-center gap-1">
                                {file.fileData.overall_processed_status !==
                                  "DONE" &&
                                file.fileData.overall_processed_status !==
                                  "FAILED" ? (
                                  <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
                                ) : null}
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    getStatusBadge(
                                      file.fileData.overall_processed_status,
                                    ).color
                                  }`}
                                >
                                  {
                                    getStatusBadge(
                                      file.fileData.overall_processed_status,
                                    ).text
                                  }
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* More Options - Show on Hover */}
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col gap-2">
                          {file.fileData && (
                            <Tooltip content="View logs">
                              <button
                                className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-purple-50 border border-purple-200 transition-all hover:scale-110"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewLogs(file);
                                }}
                                type="button"
                              >
                                <Activity className="w-4 h-4 text-purple-600" />
                              </button>
                            </Tooltip>
                          )}
                          <Can permission="documents:delete">
                            <Tooltip content="Delete File">
                              <button
                                className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 border border-red-200 transition-all hover:scale-110"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFileClick(file);
                                }}
                                type="button"
                              >
                                <Trash2Icon className="w-3 h-3 text-red-600" />
                              </button>
                            </Tooltip>
                          </Can>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUpload}
        isUploading={isUploading}
      />

      {/* Preview Modal */}
      <PreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        formatFileSize={formatFileSize}
      />

      {/* File Logs Modal */}
      <FileLogsModal
        file={selectedFileForLogs}
        onClose={handleCloseLogsModal}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDeleteFile}
        itemName={fileToDelete?.name || ""}
        itemType="File"
        isDeleting={isDeletingFile}
      />

      <FolderFormModal
        isOpen={folderFormOpen}
        mode={folderFormMode}
        initialName={
          folderFormMode === "rename" ? (folderFormTarget?.name ?? "") : ""
        }
        onClose={() => {
          if (folderFormLoading) return;
          setFolderFormOpen(false);
          setFolderFormTarget(null);
        }}
        onSubmit={handleFolderFormSubmit}
        isLoading={folderFormLoading}
      />

      {moveModalTarget?.folderId && (
        <MoveFolderModal
          isOpen={moveModalOpen}
          sourceFolderId={moveModalTarget.folderId}
          sourceFolderName={moveModalTarget.name}
          currentParentFolderId={moveModalTarget.parentFolderId ?? null}
          rootLabel={folderName}
          tree={moveTree}
          isLoadingTree={moveTreeLoading}
          onClose={() => {
            if (moveModalLoading) return;
            setMoveModalOpen(false);
            setMoveModalTarget(null);
            setMoveModalError(null);
          }}
          onSubmit={handleMoveFolderSubmit}
          isLoading={moveModalLoading}
          externalError={moveModalError}
        />
      )}

      <DeleteConfirmModal
        isOpen={showFolderDeleteModal}
        onClose={() => {
          if (isDeletingFolder) return;
          setShowFolderDeleteModal(false);
          setFolderToDelete(null);
        }}
        onConfirm={handleConfirmDeleteFolder}
        itemName={folderToDelete?.name || ""}
        itemType="Folder"
        isDeleting={isDeletingFolder}
      />
    </div>
  );
};

export default FileManagerUI;
